<template>
  <div class="mt-10">
    <v-snackbar
      v-model="snackbarCopied"
      top
      timeout="3000"
      color="success"
      class="api-snackbar"
    >
      {{ $t('api.tokenCopied') }}
    </v-snackbar>

    <v-dialog
      v-if="tokenGeneratedModal"
      v-model="tokenGeneratedModal"
      width="432"
      :transition="false"
      content-class="api-modal"
      persistent
    >
      <v-card>
        <v-card-text class="pt-5">
          <div class="api-modal-title">
            {{ $t('api.tokenGenerated') }}
          </div>

          <div class="api-modal-subtitle">
            {{ $t('api.saveYourToken') }} <span class="api-red-text">{{ $t('api.saveYourToken2') }}</span>
          </div>

          <v-text-field
            :value="token"
            dense
            hide-details
            rounded
            readonly
            class="api-modal-text-field"
            @click="copyToken"
          >
            <template #append>
              <v-btn icon small @click="copyToken">
                <v-icon size="16" color="primary">
                  far fa-copy
                </v-icon>
              </v-btn>
            </template>
          </v-text-field>

          <div class="text-center">
            <v-btn
              color="primary"
              rounded
              elevation="0"
              class="mt-3"
              @click="handleSaved"
            >
              {{ $t('api.iSaved') }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <div class="api-title">
      {{ $t('api.generateAToken') }}
    </div>

    <div class="api-container">
      <!-- НАЗВАНИЕ -->
      <span>{{ $t('api.name') }}:</span>

      <v-text-field
        v-model="name"
        autofocus
        dense
        outlined
        hide-details
        rounded
        style="max-width: 326px;"
        :error="errors.name"
        class="ml-2 mr-6"
        @input="errors.name=false"
      />
      <!-- НАЗВАНИЕ КОНЕЦ -->

      <!-- СРОК ДЕЙСТВИЯ -->
      <span style="white-space: nowrap;">{{ $t('api.expiration') }}:</span>
      <v-menu
        ref="expirationDateRef"
        v-model="expirationDateMenu"
        offset-y
        :close-on-content-click="false"
        content-class="api-menu"
      >
        <template #activator="{ on, attrs, value }">
          <v-text-field
            v-bind="attrs"
            :value="expirationDateValue"
            dense
            outlined
            hide-details
            rounded
            readonly
            class="ml-2 mr-6 api-text-field"
            v-on="on"
          >
            <template #append>
              <v-icon
                size="12"
                v-bind="attrs"
                class="api-text-field-arrow"
                :class="getArrowClass(value)"
                v-on="on"
              >
                fas fa-chevron-down
              </v-icon>
            </template>
          </v-text-field>
        </template>

        <v-card>
          <v-list dense>
            <v-list-item
              v-for="({ text, value }, i) in expirationDates"
              :key="`${value}-${i}`"
              :input-value="expirationDate === value || (value === 'upToDate' && datePicker)"
              color="primary"
              @click="handleExpirationDateClick(value)"
            >
              <v-menu
                v-if="value === 'upToDate'"
                open-on-hover
                close-delay="500"
                :close-on-content-click="false"
                offset-x
                offset-y
                nudge-top="145"
                nudge-right="10"
                content-class="api-menu-calendar"
                @input="calenderInProcess=$event"
              >
                <template #activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    style="width: 100%; min-height: inherit;"
                    class="px-4 d-flex align-center"
                    :class="{ 'api-menu-calendar-active': calenderInProcess }"
                    v-on="on"
                  >
                    <calendar-icon />

                    <v-list-item-title>{{ text }}</v-list-item-title>
                  </div>
                </template>

                <v-date-picker
                  :value="datePicker"
                  color="primary"
                  no-title
                  :show-current="minValue"
                  :min="minValue"
                  :max="maxValue"
                  class="api-date-picker"
                  @input="handleDatePickerInput"
                />
              </v-menu>

              <v-list-item-title v-else class="px-4">
                {{ text }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
      <!-- СРОК ДЕЙСТВИЯ КОНЕЦ -->

      <v-btn
        color="primary"
        rounded
        elevation="0"
        style="max-width: 222px"
        :loading="loading"
        @click="handleGenerate"
      >
        {{ $t('api.toGenerateAToken') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import CalendarIcon from "@/components/icons/CalendarIcon.vue";

export default {
  name: 'ApiGenerate',

  components: {
    CalendarIcon
  },

  data() {
    return {
      loading: false,
      name: '',
      expirationDate: '30days',
      expirationDateMenu: false,
      calenderInProcess: false,
      datePicker: null,
      tokenGeneratedModal: false,
      snackbarCopied: false,
      token: '',
      errors: {
        name: false
      },
    }
  },

  computed: {
    expirationDates() {
      return [
        {
          text: this.$t('api.expirationDates.7days'),
          value: '7days'
        },
        {
          text: this.$t('api.expirationDates.30days'),
          value: '30days'
        },
        {
          text: this.$t('api.expirationDates.90days'),
          value: '90days'
        },
        {
          text: this.$t('api.expirationDates.180days'),
          value: '180days'
        },
        {
          text: this.$t('api.expirationDates.365days'),
          value: '365days'
        },
        {
          text: this.$t('api.expirationDates.upToDate'),
          value: 'upToDate'
        },
      ]
    },

    expirationDateValue() {
      if (this.datePicker) {
        return moment(this.datePicker).format('DD.MM.YYYY')
      } else {
        return this.$t(`api.expirationDates.${this.expirationDate}`)
      }
    },

    minValue() {
      return new moment().add(1, 'days').format('YYYY-MM-DD')
    },

    maxValue() {
      return new moment().add(365, 'days').format('YYYY-MM-DD')
    }
  },

  methods: {
    getArrowClass(value) {
      return {
        'api-text-field-arrow-up': value,
        'api-text-field-arrow-down': this.$refs && this.$refs.expirationDateRef && this.$refs.expirationDateRef.isBooted && !value
      }
    },

    handleExpirationDateClick(value) {
      if (value !== 'upToDate') {
        this.expirationDate = value
        this.datePicker = null
        this.expirationDateMenu = false
      }
    },

    handleDatePickerInput(value) {
      this.datePicker = value
      this.expirationDate = null
      this.expirationDateMenu = false
    },

    async handleGenerate() {
      if (!this.name || !this.name.trim()) {
        this.errors.name = true
        return
      }

      try {
        this.loading = true
        const payload = {
          name: this.name
        }

        if (this.datePicker) {
          const today = moment().format('YYYY-MM-DD')
          payload.days = moment(this.datePicker).diff(moment(today), 'days')
        } else {
          payload.days = parseInt(this.expirationDate, 10)
        }

        const { data } = await this.api.post('/personal-access-tokens', payload)

        if (data.data.accessToken) {
          this.name = ''
          this.token = data.data.accessToken
          this.copyToken()
          this.$store.dispatch('api/loadTokens')

          this.tokenGeneratedModal = true
        }

      } catch (error) {
        // do not nothing
      } finally {
        this.loading = false
      }
    },

    handleSaved() {
      this.copyToken()
      this.tokenGeneratedModal = false
    },

    copyToken() {
      navigator.clipboard.writeText(this.token).then(() => {
        this.snackbarCopied = true
      })
    }
  }
}
</script>

<style scoped>
.api-title {
  font-weight: 500;
  font-size: 14px;
  color: #212121;
}
.api-container {
  display: flex;
  align-items: center;
  background: #F2F2F2;
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;
}
.calendar-icon {
  width: 20px;
  height: 20px;
  margin-right: 6px;
}
</style>

<style>
.api-menu.v-menu__content,
.api-menu-calendar.v-menu__content {
  border-radius: 4px;
  border: 1px solid rgba(16, 24, 40, 0.1);
  box-shadow: 0px 4px 10px rgba(33, 69, 98, 0.08);
}

.api-menu .v-list > .v-list-item {
  padding: 0;
}
.api-menu-calendar-active {
  color: #b645ee !important;
  caret-color: #b645ee !important;
  background-color: rgba(182,69,238,.16);
  min-height: inherit;
}
.api-text-field {
  max-width: 145px;
}
.api-text-field > .v-input__control > .v-input__slot,
.api-text-field input {
  cursor: pointer;
}
.api-text-field > .v-input__control > .v-input__slot {
  padding: 0 !important;
}
.api-text-field > .v-input__control > .v-input__slot > .v-text-field__slot input {
  padding-left: 16px !important;
}
.api-text-field .v-input__append-inner {
  margin: 0 !important;
  padding: 0 !important;
  min-height: inherit;
}
.api-text-field-arrow {
  padding-right: 16px !important;
  color: currentColor !important;
}
.api-text-field-arrow-up::before {
  animation-name: rotateUp;
  animation-duration: .3s;
  animation-fill-mode: forwards;
}
.api-text-field-arrow-down::before {
  animation-name: rotateDown;
  animation-duration: .3s;
  animation-fill-mode: forwards;
}
@keyframes rotateUp {
  from {
    margin-bottom: 0px;
    transform: rotate(0deg);
  }
  to {
    margin-bottom: 2px;
    transform: rotate(180deg);
  }
}
@keyframes rotateDown {
  from {
    margin-bottom: 2px;
    transform: rotate(180deg);
  }
  to {
    margin-bottom: 0px;
    transform: rotate(0deg);
  }
}
.api-modal {
  border-radius: 20px;
}
.api-modal-title {
  font-weight: 500;
  font-size: 24px;
  color: #000000;
}
.api-modal-subtitle {
  margin-top: 10px;
  font-weight: 400;
  font-size: 13px;
  line-height: 15px;
  color: #ACACAC;
}
.api-modal-subtitle .api-red-text {
  color: #EA0B0B;
}
.api-modal-text-field {
  margin-top: 12px;
  background: rgba(238, 238, 238, 0.52);
}
.api-modal-text-field:hover {
  background: rgba(238, 238, 238, 0.8);
}
.api-modal-text-field .v-input__control {
  height: 36px;
}
.api-modal-text-field .v-input__slot {
  margin-top: auto !important;
  margin-bottom: auto !important;
}
.api-snackbar .v-snack__wrapper {
  border-radius: 6px;
  min-height: unset !important;
  min-width: unset !important;
}
.api-snackbar .v-snack__content {
  font-weight: 500;
  padding: 8px 12px;
}
.api-snackbar .v-snack__action {
  display: none;
}
.api-date-picker .v-date-picker-table__current.v-btn--outlined {
  border: none;
}
.api-date-picker .v-date-picker-table__current.v-btn--outlined.primary--text {
  color: inherit !important;
  caret-color: inherit !important;
}
</style>
